import { useTranslation } from "react-i18next";

import { Flex, Typography, TypographyVariants } from "@aircall/tractor";

import {
  PlaybookResultFragment,
  TopicResultFragment,
} from "@/graphql/generated/graphql";
import { useGetLicenses } from "@/hooks/useGetLicenses/useGetLicenses";
import { useSavePlaybookResult } from "@/hooks/useSavePlaybookResult/useSavePlaybookResult";
import { EditableSelect, EditableTextarea } from "@dashboard/library";
import { useMemo } from "react";

type PlaybookResultFormFieldProps = {
  topicResult: TopicResultFragment;
  onUpdate: (newValue: string | boolean) => void;
  readOnly?: boolean;
  variants: {
    title: TypographyVariants;
    field: TypographyVariants;
  };
};

export function PlaybookResultFormField({
  topicResult,
  onUpdate,
  readOnly = false,
  variants,
}: PlaybookResultFormFieldProps) {
  const {
    name,
    content: { response },
  } = topicResult;

  const { t } = useTranslation();

  const stringValue = useMemo(() => {
    if (response?.__typename === "StringValue") {
      return response.stringValue;
    }
    if (response?.__typename === "BooleanValue") {
      return response.booleanValue
        ? t("PlaybookResults.Yes")
        : t("PlaybookResults.No");
    }
    return undefined;
  }, [response, t]);

  return (
    <Flex flexDirection="column" gap={1}>
      <Typography variant={variants.title} color="text-highlight">
        {name}
      </Typography>

      {readOnly ? (
        <Typography
          variant={variants.field}
          color="text-base"
          whiteSpace="pre-line"
        >
          {stringValue}
        </Typography>
      ) : (
        <>
          {response.__typename === "StringValue" && (
            <EditableTextarea
              defaultValue={response?.stringValue ?? undefined}
              onSubmit={(newValue) => onUpdate(newValue as string)}
              variant={variants.field}
            />
          )}

          {response.__typename === "BooleanValue" && (
            <EditableSelect<boolean>
              defaultValue={response?.booleanValue ?? undefined}
              placeholder={t("call_context.summary_empty_message")}
              onChange={onUpdate}
              options={[
                {
                  label: t("Yes"),
                  value: true,
                },
                {
                  label: t("No"),
                  value: false,
                },
              ]}
            />
          )}
        </>
      )}
    </Flex>
  );
}

export type PlaybookResultsFormProps = {
  playbookResult: Omit<
    PlaybookResultFragment,
    "callUuid" | "language" | "name"
  >;
  variants?: {
    title: TypographyVariants;
    field: TypographyVariants;
  };
};

export function PlaybookResultsForm({
  playbookResult,
  variants = {
    title: "bodySemiboldS",
    field: "bodyRegularS",
  },
}: PlaybookResultsFormProps) {
  const { savePlaybookResult } = useSavePlaybookResult();
  const { hasLicense } = useGetLicenses();
  const canUpdatePlaybook = hasLicense("ai_assist_pro");

  const handleUpdate = async (
    topicResult: TopicResultFragment,
    newValue: string | boolean
  ) => {
    if (!canUpdatePlaybook) {
      return;
    }

    // Get current value based on response type
    let currentValue: string | boolean | undefined;
    if (topicResult.content.response.__typename === "StringValue") {
      currentValue = topicResult.content.response.stringValue;
    } else if (topicResult.content.response.__typename === "BooleanValue") {
      currentValue = topicResult.content.response.booleanValue;
    }

    // Do nothing if the value hasn't changed
    if (currentValue === newValue) {
      return;
    }

    // Prepare the topic result input based on the response type
    const topicResultInput = {
      id: topicResult.id,
      ...(typeof newValue === "string"
        ? { responseString: newValue }
        : { responseBoolean: newValue }),
    };

    await savePlaybookResult({
      input: {
        id: playbookResult.id,
        topicResults: [topicResultInput],
      },
    });
  };

  return (
    <Flex flexDirection="column" gap="s" w="100%">
      {playbookResult.topicResults.map((topicResult) => {
        return (
          <PlaybookResultFormField
            key={topicResult.id}
            topicResult={topicResult}
            onUpdate={(newValue) => handleUpdate(topicResult, newValue)}
            readOnly={!canUpdatePlaybook}
            variants={variants}
          />
        );
      })}
    </Flex>
  );
}
